import shortid from 'shortid';

import { apiRequest } from '.';

import {
  PROFILE_API_URL,
  PROFILE_RESUME_JSO_API_URL,
} from '../constants';
import sleep from '../sleep';
import RequestLimitError from './RequestLimitError';

const formatProfileResponse = (profile) => (
  Object.keys(profile).reduce((obj, key) => {
    const prop = profile[key];
    // negotiable (value: '-1') was removed in JS-5566
    // if the js had negotiable, removing it here will not leave '-1' in any app that calls this
    // doing it here will keep all the app logic reading desired_salary consistent, otherwise we'd have to check for it everywhere
    // remove if backend removes negotiable values in existing job seekers
    if (true && key === 'desired_salary' && prop === '-1') {
      return obj;
    }
    return {
      // spread previous reduction into new object
      ...obj,
      // add ids to array elements to use as keys
      [key]: Array.isArray(prop) ? (
        // property is an array
        prop.map((value) => ({
          reactKey: shortid.generate(),
          // override id if value has id (like in licenses)
          ...value,
        }))
      ) : (
        // property is not an array, so just add to object
        profile[key]
      ),
    };
  }, {})
);

async function makeProfileRequest(apiUrl, id, method = 'GET', body) {
  const fetchInit = {
    method,
    headers: {
      Accept: 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  };

  const response = await apiRequest(
    apiUrl + (id ? `/${id}` : ''),
    fetchInit,
  );

  // 204 (no content) means PATCH was successful
  if (response.status === 204) {
    return null;
  }

  const json = await response.json();
  const { profile } = json.data;

  return formatProfileResponse(profile);
}

async function makeParsedResumeRequest(apiUrl) {
  const fetchInit = {
    method: 'get',
    headers: {
      Accept: 'application/json',
    },
  };

  const response = await apiRequest(apiUrl, fetchInit);
  const json = await response.json();
  const { parsed_resume: parsedResume } = json.data;
  return formatProfileResponse(parsedResume);
}

// limit polling to 8 requests
// this will be 12.8 seconds for the last attempt with exponential backoff
const ATTEMPT_LIMIT = 8;
// first backoff will be 100 ms
const BASE_BACKOFF = 100;

async function pollResumeJsoRequest(
  apiUrl, id, backoff = BASE_BACKOFF, attemptLimit = ATTEMPT_LIMIT, attempt = 1,
) {
  const fetchInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  const response = await apiRequest(
    apiUrl + (id ? `/${id}` : ''),
    fetchInit,
    `attempt=${attempt}`,
  );

  const json = await response.json();
  const { profile } = json.data;
  const { upload_parse_status: uploadParseStatus } = profile;

  // stop polling with either of these statuses
  if (uploadParseStatus === 'success' || uploadParseStatus === 'failed') {
    // if success, then continue with the verify flow
    // if failed, then the app will switch back to profile mode
    return formatProfileResponse(profile);
  }

  // throw error if next request will reach limit
  if ((attempt + 1) >= attemptLimit) {
    throw new RequestLimitError();
  }

  // this means the status is one of not_started, queued, pending
  // so retry with exponential backoff
  await sleep(backoff);
  return pollResumeJsoRequest(apiUrl, id, backoff * 2, attemptLimit, attempt + 1);
}

async function pollResumeJsoRightAfterUploadRequest(apiUrl, id) {
  await sleep(3000);
  const profileJso = await pollResumeJsoRequest(apiUrl, id, 500, 4);
  return profileJso;
}

// API Services
export const fetchProfile = (id) => makeProfileRequest(PROFILE_API_URL, id);
export const fetchParsedResume = (id) => makeParsedResumeRequest(`${PROFILE_RESUME_JSO_API_URL}/${id}`);
export const patchProfile = (data) => makeProfileRequest(PROFILE_API_URL, null, 'PATCH', data);
export const validatePatchData = (data) => makeProfileRequest(PROFILE_API_URL, null, 'PATCH', {
  ...data, validate_only: true,
});
export const pollResumeJso = (id) => pollResumeJsoRequest(
  PROFILE_API_URL,
  id,
);
export const pollResumeJsoRightAfterUpload = (id) => pollResumeJsoRightAfterUploadRequest(
  PROFILE_API_URL,
  id,
);
